import moment from 'moment';
import {
    AggregatedIssue,
    CrawlRun,
    CrawlerHealth,
    HealthLevel,
    IssueKind,
    SampleError,
    TrendPoint,
} from '../models/crawler-mon.model';

/**
 * Health rules and error-category knowledge for the nightly lnwstore crawlers.
 *
 * Pure functions returning plain data — the component owns all wording, so this
 * stays testable without Angular. The crawler owns each run's verdict: this
 * module passes `status` through untouched and only adds the checks a run
 * record structurally cannot make, namely "it never ran" and cross-run drift.
 */

/** Crawlers on the nightly systemd timer, in pipeline order. */
export const NIGHTLY_CRAWLERS = ['product', 'sales', 'order'] as const;

/** Thailand has no DST, so a fixed offset is exact. */
const BKK_UTC_OFFSET_MINUTES = 7 * 60;

/** `deploy/lnw-crawler.timer`: OnCalendar=*-*-* 02:00:00 Asia/Bangkok. */
const SCHEDULED_HOUR_BKK = 2;

/** The timer is daily, so a healthy crawler is never quiet for longer than this. */
const EXPECTED_INTERVAL_HOURS = 24;

/**
 * Slack on top of the 24h interval before silence counts as a missed run.
 *
 * Measured from the last run's own start rather than from the 02:00 slot: the
 * three crawlers run in sequence and start at 02:00, ~02:50 and ~03:11, and
 * `product` starts barely 30 seconds after the slot — comparing against the
 * slot itself would flip to "overdue" on the smallest clock skew. Elapsed time
 * still surfaces a skipped night the same morning (~06:00-07:15 ICT) and each
 * crawler's threshold self-adjusts to its own position in the pipeline.
 */
const OVERDUE_GRACE_HOURS = 4;

/** Runs used for the drift baseline and the card sparkline. */
const BASELINE_RUNS = 7;
const TREND_RUNS = 14;

/**
 * Below this share of the recent median, the card highlights the volume in red.
 *
 * A display threshold only. `product` and `sales` already run an equivalent
 * trailing-average check inside their own `evaluate()` and report `degraded`
 * themselves, so escalating here would just re-judge the same runs with a
 * different estimator (median vs mean) and let the page contradict the verdict
 * it quotes. `order` has no such check, which is why the highlight still earns
 * its place — it just informs rather than overrides.
 */
const VOLUME_DROP_RATIO = 0.5;

interface CrawlerProfile {
    description: string;
    /** The count that answers "did this run actually bring the data home?". */
    primaryMetricKey: string;
    primaryMetricLabel: string;
}

const CRAWLER_PROFILES: Record<string, CrawlerProfile> = {
    product: {
        description: 'Catalog + product pages — runs first, feeds sales',
        primaryMetricKey: 'productSaved',
        primaryMetricLabel: 'Products saved',
    },
    sales: {
        description: 'Per-product sales transactions — needs products',
        primaryMetricKey: 'sellRows',
        primaryMetricLabel: 'Sale rows',
    },
    order: {
        description: 'Order report download, incremental by month',
        primaryMetricKey: 'ordersSaved',
        primaryMetricLabel: 'Orders saved',
    },
};

export interface Diagnostic {
    kind: IssueKind;
    /** What the category actually means when the crawler emits it. */
    meaning: string;
    /** The next concrete step, naming the file to look at where there is one. */
    action: string;
}

/**
 * Error category -> what it means and what to do. Categories come from
 * `report.error("CATEGORY", ...)` calls in the crawler repo; `kind` separates
 * "the website changed, go edit a selector" from noise worth ignoring once.
 *
 * Deliberately quotes no thresholds. The ratios that decide a run's status live
 * in the crawler's `evaluate*()` functions and would rot silently here — and
 * the run's own Thai `reason`, shown verbatim on the card, already carries the
 * live numbers. File paths are kept: those fail visibly when they move.
 */
const ERROR_DIAGNOSTICS: Record<string, Diagnostic> = {
    SELECTOR_EMPTY: {
        kind: 'structure',
        meaning:
            'A catalog page loaded fine but contained no product links at all.',
        action: 'The strongest single signal that the product-list markup changed — the crawler fails the run outright when enough pages come back empty. Compare the link selector in src/shared/backstore_traverser.ts against the live catalog page. Check the session too: a logged-out page also renders zero products.',
    },
    PDATA_MISSING: {
        kind: 'structure',
        meaning:
            'A product page loaded but the embedded `vm.pdata` model was not there.',
        action: 'Open a product page and check `vm.pdata` in the browser console. If the shape moved, fix the extraction in src/shared/backstore_traverser.ts. A high enough share of pages fails the run.',
    },
    TX_MISSING: {
        kind: 'structure',
        meaning:
            'A product sales page loaded but `vm.transactions` was not there.',
        action: 'Check `vm.transactions` on a product sales page and realign src/crawlers/sales_crawler/sales_crawler.ts. A high enough share of pages fails the run.',
    },
    CATALOG_FETCH: {
        kind: 'structure',
        meaning:
            'Navigating to a catalog page, or pulling its links, threw before the page could be counted.',
        action: 'Read the sample message for the URL and the underlying error. A one-off is usually network; a repeat means the catalog URL or pagination in src/shared/backstore_traverser.ts moved.',
    },
    CATALOG_SIZE_UNEXPECTED: {
        kind: 'structure',
        meaning:
            'The catalog page count fell outside the expected range — the pagination markup probably changed.',
        action: 'Compare the pager on the catalog page against src/shared/pagination.ts. This alone marks the run degraded.',
    },
    UNMAPPED_COLUMN: {
        kind: 'structure',
        meaning:
            'The downloaded order report contained a column header the crawler does not know.',
        action: 'Add or rename the header in the column map in src/crawlers/order_crawler/order_crawler.ts. Left unfixed, that field silently stops being stored.',
    },
    AUTH_OR_FORMAT: {
        kind: 'auth',
        meaning:
            'The downloaded order file could not be opened as a workbook — usually an HTML login page arrived instead of the XLSX.',
        action: 'Almost always a lost session rather than a format change. Verify the login still works, then re-run.',
    },
    LOGIN_FAILED: {
        kind: 'auth',
        meaning: 'Login to a.lnwstore.com did not complete.',
        action: 'Check the credentials in the server .env first, then the login form selectors in src/shared/login.ts. Everything downstream fails when this does.',
    },
    NAV_TIMEOUT: {
        kind: 'transient',
        meaning: 'A page.goto() call exceeded its timeout.',
        action: 'A handful per run is normal background noise. Watch the ratio instead: once navigation failures dominate the attempts, sales fails the run, which means blocked or logged out rather than slow.',
    },
    NAV_ERROR: {
        kind: 'transient',
        meaning: 'Navigation failed for a reason other than a timeout.',
        action: 'Read the sample message for the URL. Counted together with NAV_TIMEOUT toward the navigation-failure ratio in sales.',
    },
    DOWNLOAD: {
        kind: 'transient',
        meaning:
            'The order report download came back with a non-OK HTTP status.',
        action: 'Usually transient. If it repeats, check the report URL and its query parameters in src/crawlers/order_crawler/order_crawler.ts.',
    },
    CHUNK_FAILED: {
        kind: 'data-gap',
        meaning:
            'A month of orders failed to import, so the watermark was held back.',
        action: 'The next successful run re-fetches that month, so no data is lost permanently — but the gap stays until it does. Re-run rather than wait if the month matters.',
    },
    DB_WRITE: {
        kind: 'infra',
        meaning: 'Writing to MongoDB failed.',
        action: 'Not a website problem. Check the Mongo router at router.genlogic.co.th and the TLS CA cert mounted into the container.',
    },
    FATAL: {
        kind: 'unknown',
        meaning:
            'The run aborted on an unhandled error before it could finish normally.',
        action: 'Read the sample message — it usually names the failing selector or URL, which points at the real category.',
    },
};

const UNKNOWN_DIAGNOSTIC: Diagnostic = {
    kind: 'unknown',
    meaning: 'This error category is not in the monitor’s catalogue yet.',
    action: 'Read the sample message below, then grep the category in lnw-crawler-v2/src to find where it is raised.',
};

/** Always resolves — an unrecognised category degrades to a usable hint. */
export function diagnosticFor(category: string): Diagnostic {
    return ERROR_DIAGNOSTICS[category] ?? UNKNOWN_DIAGNOSTIC;
}

/** Worst-first, so one array sort covers cards, banner and issue table. */
const LEVEL_SEVERITY: Record<HealthLevel, number> = {
    failed: 4,
    overdue: 4,
    degraded: 2,
    unknown: 1,
    ok: 0,
};

const KIND_SEVERITY: Record<IssueKind, number> = {
    structure: 5,
    auth: 4,
    'data-gap': 3,
    infra: 2,
    unknown: 1,
    transient: 0,
};

export function levelSeverity(level: HealthLevel): number {
    return LEVEL_SEVERITY[level];
}

/**
 * Playwright writes ANSI dim codes into its call logs, which reach Mongo
 * verbatim and render as `[2m` garbage in the browser.
 */
// eslint-disable-next-line no-control-regex
const ANSI_PATTERN = /\[[0-9;]*m/g;

export function cleanMessage(message: string): string {
    return String(message ?? '')
        .replace(ANSI_PATTERN, '')
        .trim();
}

/** The most recent 02:00 ICT slot that has already come round. */
function lastScheduledSlot(now: moment.Moment): moment.Moment {
    const bkkNow = now.clone().utcOffset(BKK_UTC_OFFSET_MINUTES);
    const slot = bkkNow.clone().startOf('day').add(SCHEDULED_HOUR_BKK, 'hours');
    return slot.isAfter(bkkNow) ? slot.subtract(1, 'day') : slot;
}

/** The next 02:00 ICT slot, for the "next run expected" line in the banner. */
export function nextScheduledSlot(now: moment.Moment): moment.Moment {
    return lastScheduledSlot(now).add(1, 'day');
}

/**
 * A run record can only report runs that happened. If the container never
 * started, `crawl_runs` simply gains nothing and the last row stays green —
 * so silence past the grace window is its own alert.
 */
function isOverdue(lastRun: CrawlRun | null, now: moment.Moment): boolean {
    // No history at all is reported as "unknown" rather than "overdue" — there
    // is no evidence a run was ever expected.
    if (!lastRun) {
        return false;
    }
    const hoursSinceStart = now.diff(moment(lastRun.startedAt), 'hours', true);
    return hoursSinceStart > EXPECTED_INTERVAL_HOURS + OVERDUE_GRACE_HOURS;
}

function median(values: number[]): number | null {
    if (values.length === 0) {
        return null;
    }
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0
        ? (sorted[mid - 1] + sorted[mid]) / 2
        : sorted[mid];
}

/** Group a run's samples once, so filling each category's examples is O(n). */
function samplesByCategory(run: CrawlRun): Map<string, SampleError[]> {
    const grouped = new Map<string, SampleError[]>();
    for (const sample of run.sampleErrors ?? []) {
        const bucket = grouped.get(sample.category);
        if (bucket) {
            bucket.push(sample);
        } else {
            grouped.set(sample.category, [sample]);
        }
    }
    return grouped;
}

const MAX_SAMPLES_PER_ISSUE = 3;

function buildIssues(
    crawler: string,
    runs: CrawlRun[],
    latestRun: CrawlRun | null,
): AggregatedIssue[] {
    const byCategory = new Map<string, AggregatedIssue>();

    for (const run of runs) {
        const categories = Object.entries(run.errors ?? {});
        if (categories.length === 0) {
            continue;
        }
        const grouped = samplesByCategory(run);

        for (const [category, count] of categories) {
            let issue = byCategory.get(category);
            if (!issue) {
                issue = {
                    crawler,
                    category,
                    kind: diagnosticFor(category).kind,
                    total: 0,
                    runCount: 0,
                    active: Boolean(latestRun?.errors?.[category]),
                    // `runs` arrives newest-first, so the first hit is the latest.
                    lastSeen: run.startedAt,
                    sampleMessages: [],
                };
                byCategory.set(category, issue);
            }
            issue.total += count;
            issue.runCount += 1;

            const room = MAX_SAMPLES_PER_ISSUE - issue.sampleMessages.length;
            if (room > 0) {
                for (const sample of (grouped.get(category) ?? []).slice(
                    0,
                    room,
                )) {
                    issue.sampleMessages.push(cleanMessage(sample.message));
                }
            }
        }
    }

    return [...byCategory.values()].sort(
        (a, b) =>
            Number(b.active) - Number(a.active) ||
            KIND_SEVERITY[b.kind] - KIND_SEVERITY[a.kind] ||
            b.total - a.total,
    );
}

/** Build the health model for one crawler from its runs (newest first). */
export function buildHealth(
    crawler: string,
    allRuns: CrawlRun[],
    completedThrough: string | null,
    now: moment.Moment,
): CrawlerHealth {
    const profile = CRAWLER_PROFILES[crawler] ?? {
        description: '',
        primaryMetricKey: '',
        primaryMetricLabel: 'Records',
    };
    const runs = allRuns.filter((run) => run.crawler === crawler);
    const lastRun = runs[0] ?? null;
    const lastOkRun = runs.find((run) => run.status === 'ok') ?? null;

    const metricOf = (run: CrawlRun): number | null => {
        const value = run.counts?.[profile.primaryMetricKey];
        return typeof value === 'number' ? value : null;
    };

    const latestValue = lastRun ? metricOf(lastRun) : null;
    // Baseline skips the latest run (that is what we are judging) and failed
    // runs, whose zeroes would drag the bar down and mask the next real drop.
    const baseline = median(
        runs
            .slice(1)
            .filter((run) => run.status !== 'failed')
            .slice(0, BASELINE_RUNS)
            .map(metricOf)
            .filter((value): value is number => value !== null),
    );
    const hasBaseline =
        latestValue !== null && baseline !== null && baseline > 0;
    const deltaPct = hasBaseline
        ? ((latestValue - baseline) / baseline) * 100
        : null;
    const volumeDrop =
        hasBaseline && latestValue < VOLUME_DROP_RATIO * baseline;

    const trend: TrendPoint[] = runs
        .slice(0, TREND_RUNS)
        .reverse()
        .map((run) => ({
            label: moment(run.startedAt).format('D MMM HH:mm'),
            value: metricOf(run) ?? 0,
        }));

    let level: HealthLevel;
    if (!lastRun) {
        level = 'unknown';
    } else if (isOverdue(lastRun, now)) {
        level = 'overdue';
    } else {
        // Straight passthrough of the crawler's own verdict.
        level = lastRun.status === 'ok' ? 'ok' : lastRun.status;
    }

    return {
        crawler,
        description: profile.description,
        level,
        lastRun,
        lastOkRun,
        primaryMetricLabel: profile.primaryMetricLabel,
        latestValue,
        deltaPct,
        volumeDrop,
        trend,
        issues: buildIssues(crawler, runs, lastRun),
        completedThrough,
    };
}
